// Dialogy výsledku hovoru: Nezvedla, Zavolat jindy, Nemá zájem, Nový kontakt (mockup v8, obrazovky 3 až 5).
import React, { useEffect, useState } from "react";
import { PhoneMissed, CalendarClock, UserX, Send, Clock, UserPlus, ChevronDown, Pencil } from "lucide-react";
import { Modal, HlavickaOkna, Field } from "../ui.jsx";
import DatePicker from "../DatePicker.jsx";
import { toast } from "../Toast.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import { salesGet, salesPost, dnesIso, addDays, dowOf, denDlouze } from "./salesApi.js";
import { Oznameni, Cipy, Volby, Pil } from "./salesUi.jsx";

export function SmsBublina({ text }) {
  return (
    <div className="rounded-2xl rounded-bl-md border border-sky-200 bg-sky-50 px-3.5 py-3">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-sky-700">
        <Send className="h-3.5 w-3.5" />SMS z čísla sales 777 987 649
      </div>
      <div className="text-[15px] leading-relaxed text-slate-800">{text}</div>
    </div>
  );
}

const CASY = (() => { const o = []; for (let m = 7 * 60; m <= 20 * 60; m += 30) o.push(`${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`); return o; })();
export function VyberCasu({ value, onChange, popisek = "Čas (nepovinné)", povinny = false }) {
  return (
    <div>
      <span className="label">{popisek}</span>
      <div className="vyber">
        <Clock />
        <span className="min-w-0 flex-1 truncate">{value || "bez času"}</span>
        <span className="vyber-sipka"><ChevronDown /></span>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={popisek}>
          {!povinny && <option value="">bez času</option>}
          {CASY.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </div>
  );
}

export function NezvedlaDialog({ lead, open, onClose, onHotovo }) {
  const [nahled, setNahled] = useState(null);
  const [datum, setDatum] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open || !lead) return;
    setChyba(""); setNahled(null);
    salesGet("nezvedla_nahled", { id: lead.id }).then((n) => { setNahled(n); setDatum(n.dalsi || ""); }).catch((e) => setChyba(e.message));
  }, [open, lead]);
  const zmenDatum = async (d) => {
    setDatum(d);
    try { setNahled(await salesGet("nezvedla_nahled", { id: lead.id, datum: d })); } catch (e) { setChyba(e.message); }
  };
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("nezvedla", { id: lead.id, dalsi: datum });
      const sms = (r.kroky || []).find((k) => k.krok === "sms");
      if (sms && !sms.ok) toast(`Zapsáno, ale SMS neodešla: ${sms.chyba || "neznámá chyba"}. Pošli ji prosím z telefonu.`, "error");
      else toast(r.uzavreno ? "Zapsáno, kontakt uzavřen." : r.kolo2 && r.odlozeno ? `Zapsáno, 2. kolo ${denDlouze(r.odlozeno)}.` : sms ? "Zapsáno a SMS odeslána." : "Zapsáno.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  const posledni = nahled?.posledni;
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={PhoneMissed} nazev={posledni ? `Nezvedla ani ${nahled?.max || 4}. hovor${nahled?.kolo2 ? " ve 2. kole" : ""}` : "Nezvedla"} popis={`${lead.jmeno}${nahled ? `, ${nahled.pokus}. pokus ze ${nahled.max || 4}` : ""}`} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !nahled} aria-busy={busy}>
          {posledni ? (nahled?.kolo2Pak ? "Zapsat, 2. kolo za 2 měsíce" : "Zapsat a uzavřít") : nahled?.sms ? <><Send className="h-4 w-4" />Zapsat a poslat SMS</> : "Zapsat"}
        </button>
      </>}>
      <div className="space-y-3">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {!nahled && !chyba && <div className="h-24 animate-pulse rounded-xl bg-brand-light" />}
        {/* 15. 10. 2026: typ kontaktu (nový, po zkušebním, bývalá, aktivní) řídí SMS a konec pokusů. */}
        {nahled && nahled.typ && nahled.typ !== "novy" && (
          <Pil ton={nahled.typ === "aktivni" ? "ok" : nahled.typ === "byvala" ? "zlata" : "info"} casti={nahled.typ === "aktivni" ? "aktivní klientka" : nahled.typ === "byvala" ? "bývalá klientka" : "byla na zkušebním"} />
        )}
        {nahled && posledni && (
          <Oznameni ton="info">{nahled.typ === "po_zkusebnim"
            ? <>Kontakt se přesune mezi <b>Odložené</b> a znovu se objeví k volání za 3 měsíce. SMS se neposílá.</>
            : nahled.typ === "byvala"
              ? <>Kontakt se vrátí mezi <b>Bývalé klientky</b>. SMS se neposílá, NQ lead nevznikne.</>
              : nahled.typ === "aktivni"
                ? <>Kontakt zmizí z volání, je to aktivní klientka. SMS se neposílá.</>
                : nahled.kolo2
                  ? <>Nezvedla ani ve <b>2. kole</b>. Kontakt se uzavře jako NQlead s důvodem <b>nedovoláno</b> natrvalo. SMS se už neposílá.</>
                  : nahled.kolo2Pak
                    ? <>Ani jednou nezvedla, kontakt se proto vrátí k volání <b>za 2 měsíce</b> jako 2. kolo (2 pokusy). SMS se teď neposílá, ke kontaktu se připne poznámka.</>
                    : <>Kontakt se uzavře jako NQlead s důvodem <b>nedovoláno</b>. SMS se už neposílá. Když se paní ozve sama, najdeš ji v Kontaktech a vrátíš k volání.</>}</Oznameni>
        )}
        {nahled && !posledni && (
          <>
            <div>
              <span className="label">Další hovor</span>
              <DatePicker value={datum} min={addDays(dnesIso(), 1)} onChange={zmenDatum} />
            </div>
            <div className="text-[13px] text-slate-500">
              Appka navrhla {denDlouze(nahled.navrh)}{(nahled.typ && nahled.typ !== "novy") || nahled.kolo2 ? ", za týden" : " podle pravidla pokusů"}. Datum můžeš změnit.{nahled.sms ? " Když datum změníš, den v SMS se změní s ním." : ""}
              {[6, 7].includes(dowOf(datum)) ? " Pozor, vybrala jsi víkend." : ""}
            </div>
            {nahled.sms ? <SmsBublina text={nahled.sms} /> : <Oznameni ton="info">{nahled.typ && nahled.typ !== "novy" ? "SMS se neposílá, jen se zapíše další hovor." : "Po 2. pokusu se SMS neposílá, jen se zapíše další hovor."}</Oznameni>}
          </>
        )}
      </div>
    </Modal>
  );
}

const RYCHLE = [
  { key: "dnes", label: "Dnes později" }, { key: "zitra", label: "Zítra" }, { key: "tyden", label: "Za týden" },
  { key: "2tydny", label: "Za 2 týdny" }, { key: "mesic", label: "Za měsíc" }, { key: "vlastni", label: "Vlastní datum" },
];
function datumZRychle(k) {
  const d = dnesIso();
  if (k === "dnes") return d;
  if (k === "zitra") return addDays(d, 1);
  if (k === "tyden") return addDays(d, 7);
  if (k === "2tydny") return addDays(d, 14);
  if (k === "mesic") return addDays(d, 30);
  return "";
}
export function JindyDialog({ lead, open, onClose, onHotovo }) {
  // Nový kontakt, se kterým se ještě nemluvilo: tímhle dialogem se plánuje první hovor.
  const prvni = !!lead && lead.stav === "novy" && !lead.pokus;
  const [volba, setVolba] = useState("vlastni");
  const [datum, setDatum] = useState("");
  const [cas, setCas] = useState("");
  const [pozn, setPozn] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open || !lead) return;
    // 1. 10. 2026: nový kontakt bez data, výchozí první hovor dnes.
    setVolba("vlastni"); setDatum(lead.stav === "odlozeno" && lead.dalsi > dnesIso() ? lead.dalsi : (lead.stav === "novy" && !lead.dalsi ? dnesIso() : addDays(dnesIso(), 1))); setCas(""); setPozn(lead.poznamka || ""); setChyba("");
  }, [open, lead]);
  const rychle = (k) => { setVolba(k || "vlastni"); if (k && k !== "vlastni") setDatum(datumZRychle(k)); };
  const uloz = async () => {
    // 17. 10. 2026 (Láďa): už naplánovaný budoucí hovor se přepisuje jen po potvrzení.
    if (lead.stav === "odlozeno" && lead.dalsi && lead.dalsi > dnesIso() && lead.dalsi !== datum) {
      const f = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}. ${y}`; };
      const ok = await confirmDialog({ title: "Přepsat naplánovaný hovor?", description: `${String(lead.jmeno || "Kontakt").split(" ")[0]} už má hovor naplánovaný na ${f(lead.dalsi)}. Přesune se na ${f(datum)}, druhý nevznikne.`, confirmLabel: `Přepsat na ${f(datum)}`, variant: "warning" });
      if (!ok) return;
    }
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("jindy", { id: lead.id, datum, cas, poznamka: pozn });
      const smsHned = (r?.kroky || []).some((k) => k.krok === "sms" && k.ok);
      toast(prvni ? (smsHned ? "Naplánováno, SMS, že se ozveš, odešla hned." : "Naplánováno. V ten den ráno v 9:00 jí odejde SMS, že se ozveš.") : "Uloženo, v ten den ji najdeš v Plánu.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarClock} nazev={prvni ? "Naplánovat první hovor" : "Zavolat jindy"} popis={lead.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !datum} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <div><span className="label">Kdy</span><Cipy moznosti={RYCHLE} value={volba} onChange={rychle} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><span className="label">Datum</span><DatePicker value={datum} min={dnesIso()} onChange={(d) => { setDatum(d); setVolba("vlastni"); }} /></div>
          <VyberCasu value={cas} onChange={setCas} />
        </div>
        <Field label="Poznámka" hint="Uvidíš ji v Plánu u jména i nahoře na kartě kontaktu.">
          <textarea className="field min-h-[76px]" value={pozn} onChange={(e) => setPozn(e.target.value)} placeholder="Např. je v práci, volat až po 16:30" />
        </Field>
      </div>
    </Modal>
  );
}

const DUVODY = [
  { key: "lokalita", label: "Nevyhovuje lokalita" }, { key: "finance", label: "Finance" }, { key: "zdravi", label: "Zdravotní důvody" },
  { key: "jinde", label: "Cvičí jinde nebo chce skupinovky" }, { key: "nevyplnovala", label: "Nevyplňovala, neví o co jde" }, { key: "jiny", label: "Jiný důvod" },
];
export function NezajemDialog({ lead, open, onClose, onHotovo }) {
  const [duvod, setDuvod] = useState("");
  const [pozn, setPozn] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setDuvod(""); setPozn(""); setChyba(""); } }, [open]);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try { const r = await salesPost("nezajem", { id: lead.id, duvod, poznamka: pozn }); toast("Kontakt uzavřen.", "success"); onHotovo?.(r); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={UserX} nazev="Nemá zájem" popis={lead.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-danger" onClick={uloz} disabled={busy || !duvod} aria-busy={busy}>Uzavřít kontakt</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Volby moznosti={DUVODY} value={duvod} onChange={setDuvod} name="Důvod" />
        <Field label="Poznámka (nepovinné)">
          <textarea className="field min-h-[64px]" value={pozn} onChange={(e) => setPozn(e.target.value)} placeholder="Např. bydlí v Brně" />
        </Field>
      </div>
    </Modal>
  );
}

// Opravit jméno kontaktu (2. 10. 2026, Láďa): leady občas vyplní nesmyslné jméno.
// Kdo smí, rozhoduje server (smi_prejmenovat v detailu kontaktu).
export function OpravitJmenoDialog({ lead, open, onClose, onHotovo }) {
  const [jmeno, setJmeno] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open && lead) { setJmeno(lead.jmeno || ""); setChyba(""); } }, [open, lead]);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("prejmenovat", { id: lead.id, jmeno });
      toast(r.beze_zmeny ? "Jméno zůstává stejné." : `Jméno opraveno na ${r.jmeno}.`, "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={Pencil} nazev="Opravit jméno" popis={lead.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || jmeno.trim().split(/\s+/).length < 2} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Field label="Jméno a příjmení">
          <input className="field" value={jmeno} onChange={(e) => setJmeno(e.target.value)} maxLength={80} autoComplete="off" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) uloz(); }} />
        </Field>
        <div className="text-[13px] text-slate-500">Změní se i v rezervaci zkušebního tréninku a v kalendáři trenéra.</div>
      </div>
    </Modal>
  );
}

export function NovyKontaktDialog({ open, onClose, onHotovo }) {
  const [f, setF] = useState({ jmeno: "", telefon: "", email: "", zdroj: "Doporučení", poznamka: "" });
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setF({ jmeno: "", telefon: "", email: "", zdroj: "Doporučení", poznamka: "" }); setChyba(""); } }, [open]);
  const zmena = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("novy_kontakt", f);
      toast(r.existoval ? "Kontakt s tímto telefonem už existuje, otevírám ho." : "Kontakt přidán do dnešního volání.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={UserPlus} nazev="Nový kontakt" popis="Přidá se do dnešního volání" onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy} aria-busy={busy}>Přidat</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Field label="Jméno a příjmení"><input className="field" value={f.jmeno} onChange={zmena("jmeno")} autoComplete="off" /></Field>
        <Field label="Telefon"><input className="field" inputMode="tel" value={f.telefon} onChange={zmena("telefon")} placeholder="777 123 456" /></Field>
        <Field label="E‑mail (nepovinné)"><input className="field" inputMode="email" value={f.email} onChange={zmena("email")} /></Field>
        <div><span className="label">Odkud o nás ví</span><Cipy moznosti={["Doporučení", "Facebook", "Instagram", "Google", "web", "Gym", "Jiné"]} value={f.zdroj} onChange={(v) => setF((x) => ({ ...x, zdroj: v || "Jiné" }))} /></div>
        <Field label="Poznámka (nepovinné)"><textarea className="field min-h-[64px]" value={f.poznamka} onChange={zmena("poznamka")} /></Field>
      </div>
    </Modal>
  );
}

// 20. 10. 2026 (Láďa): úprava dotazníku u kontaktu bez hovoru (zaplacený zkušební bez termínu,
// sales s ní po telefonu ověří, kdy může, a pak vybere termín přes její odkaz).
const DELKY_D = ["Zatím neví", "3 měsíce", "6 měsíců", "Rok"];
const FREKVENCE_D = ["1x", "2x", "3x"];
export function DotaznikDialog({ lead, open, onClose, onHotovo }) {
  const [d, setD] = useState({ kdy_zacit: "", delka: "", frekvence: "", kdy_muze: "" });
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open || !lead) return;
    const x = lead.dotaznik || {};
    const f = String(x.frekvence || "").match(/[123]/);
    // Nestandardní hodnota (např. „12 měsíců“) se nezahodí, zůstane, dokud sales nevybere jinou.
    const del = DELKY_D.find((v) => String(x.delka || "").toLowerCase().startsWith(v.toLowerCase().slice(0, 3))) || String(x.delka || "");
    setD({ kdy_zacit: x.kdy_zacit || "", delka: del, frekvence: f ? `${f[0]}x` : "", kdy_muze: x.kdy_muze || "" });
    setChyba("");
  }, [open, lead]);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("dotaznik", { id: lead.id, dotaznik: d });
      toast("Dotazník uložený.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  const nast = (k, v) => setD((p) => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={Pencil} nazev="Upravit dotazník" popis={lead.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Field label="Kdy chce začít"><input className="field" value={d.kdy_zacit} onChange={(e) => nast("kdy_zacit", e.target.value)} placeholder="Např. hned, od 12. 10." /></Field>
        <div><span className="label">Délka spolupráce</span><Cipy moznosti={DELKY_D} value={d.delka} onChange={(v) => nast("delka", v)} /></div>
        <div><span className="label">Kolikrát týdně</span><Cipy moznosti={FREKVENCE_D} value={d.frekvence} onChange={(v) => nast("frekvence", v)} /></div>
        <Field label="Kdy může trénovat"><input className="field" value={d.kdy_muze} onChange={(e) => nast("kdy_muze", e.target.value)} placeholder="Např. odpoledne po 17h, út a čt" /></Field>
      </div>
    </Modal>
  );
}
